/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { ActivatedRoute } from '@angular/router';
import {
  createDefaultGridModel,
  GoogleDocument, LIGHTGREEN_MARKER_COLOR,
  PINK_MARKER_COLOR,
  renderColoredArrow,
} from '@gh/common-internal-shared';
import {
  AppFormGroup,
  CanLeaveRoute,
  fieldStateByEntityConfig,
  fieldValidatorByEntityConfig,
  getRouteInitData,
  TextTypes,
} from '@gh/common-shared';
import { Message, MessageBasket } from '@gh/core-messages';
import { TranslationService } from '@gh/core-mls';
import {
  DialogService,
  getControl,
  NotificationService,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { isNotNil } from '@gh/core-util';
import { filter$, map$ } from '@gh/rx/operators';
import { assign, clone, concat, constant, eq, find, fromPairs, get, isEmpty, isNil, join, map, remove, sortBy } from 'lodash';
import { Observable } from 'rxjs';
import { UomSharedListItem } from '../../resources/component-and-item-details.model';
import { EstimatingImportPriceTabBusinessService } from '../../resources/estimating-import-price-tab-business.service';
import {
  createApplyItems,
  ImportPriceApplyItem,
  ImportPriceBatchId,
  ImportPriceFileItem,
  ImportPriceItemsCount,
  omitApplyItem,
} from '../../resources/estimating-import-price-tab.model';
import {
  ImportPriceNewItemsSubtabComponent,
} from './estimating-import-price-subtab/import-price-new-items-subtab/import-price-new-items-subtab.component';
import { EstimatingimportPriceTabFormState } from './estimating-import-price-tab-form.service';
import {
  buildEditableMultilineTextColumn,
  buildEditableSelectColumn,
  buildOptionSetFromArray,
  GridDefinition,
  GridDefinitionFactory,
} from '@gh/core-ui-grid';

type TabsNames = 'UPDATED_ITEM' | 'NEW_ITEM' | 'ERROR';
type SubTab = { type: TabsNames; label: string };
type SubmitState = {
  newItems: AppFormGroup[];
  updatedItems: ImportPriceApplyItem[];
};

const STATUS_UPDATED_ITEM = 'UPDATED_ITEM';
const STATUS_NEW_ITEM = 'NEW_ITEM';
const STATUS_ERROR = 'ERROR';

@Component({
  selector:      'gh-estimating-import-price-tab',
  template:   require('./estimating-import-price-tab.component.html'),
  styles:     [require('./estimating-import-price-tab.component.scss')],
  viewProviders: [
    EstimatingimportPriceTabFormState,
  ],
})
export class EstimatingImportPriceTabComponent implements OnInit, CanLeaveRoute, OnDestroy {
  subtabs: SubTab[] = [{
    type:  STATUS_UPDATED_ITEM,
    label: 'lbl_updated_items',
  }, {
    type:  STATUS_NEW_ITEM,
    label: 'lbl_new_items',
  }, {
    type:  STATUS_ERROR,
    label: 'lbl_errors',
  }];
  currentTab: TabsNames = STATUS_UPDATED_ITEM;
  uoms: UomSharedListItem[];
  submitState: SubmitState = {
    newItems:     [],
    updatedItems: [],
  };
  updatedItemsGridDefinition: GridDefinition;
  items: Hash<ImportPriceApplyItem[]> = {
    [STATUS_UPDATED_ITEM]: [],
    [STATUS_NEW_ITEM]:     [],
    [STATUS_ERROR]:        [],
  };
  counts: { [status: string]: number };
  batchId: string;
  file?: File;
  googleSheet?: GoogleDocument;

  IMPORT_PRICE_APPLY_ENTITY_CONFIG = {
    name:  {
      editable: (entity: any) => this.isUpdatedItemSelected(entity),
      required: false,
      type:     TextTypes.Text255,
    },
    memo:  {
      editable: (entity: any) => this.isUpdatedItemSelected(entity),
      required: false,
      type:     TextTypes.Text,
    },
    uomId: { editable: (entity: any) => this.isUpdatedItemSelected(entity), required: false },
  };

  importPriceApplyValidateField = fieldValidatorByEntityConfig(this.IMPORT_PRICE_APPLY_ENTITY_CONFIG);
  importPriceApplyItemState = fieldStateByEntityConfig(this.IMPORT_PRICE_APPLY_ENTITY_CONFIG);

  @ViewChild(ImportPriceNewItemsSubtabComponent, { static: false }) importPriceNewItemsComponent: ImportPriceNewItemsSubtabComponent;

  constructor(public form: EstimatingimportPriceTabFormState,
    private businessService: EstimatingImportPriceTabBusinessService,
    private waitingService: WaitingService,
    private gridDefinitionFactory: GridDefinitionFactory,
    private dialogService: DialogService,
    private notificationService: NotificationService,
    private translationService: TranslationService,
    private route: ActivatedRoute,
  ) {
  }

  get isFileSelected(): boolean {
    return isNotNil(this.file) || isNotNil(this.googleSheet);
  }

  get fileName(): string {
    return this.file && this.file.name || this.googleSheet && this.googleSheet.name || '';
  }

  get actionButtonTitle(): string {
    return this.batchId ? 'lbl_apply' : 'lbl_import_prices';
  }

  get isPriceFileImported(): boolean {
    return Boolean(this.batchId);
  }

  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
    if (!this.isPriceFileImported) {
      return true;
    }
    return this.dialogService.confirmation(yesNoConfirmation('msg_import_prices_data_will_be_lost')).pipe(
      map$(yesAnswer));
  }

  /**
   * Init component
   */
  ngOnInit(): void {
    const { ...init } = getRouteInitData(this.route);
    assign(this, init);
  }

  ngOnDestroy(): void {
    this._clearItems();
  }

  getItems(status: string): ImportPriceApplyItem[] {
    return this.items[status];
  }

  getErrors(): ImportPriceApplyItem[] {
    return sortBy(this.items.ERROR, (error) => error.batchRowNumber);
  }

  onImportOrApply(): void {
    if (this.isPriceFileImported) {
      this.onSubmit();
    } else {
      this.onImportPrices();
    }
  }

  onImportPrices(): void {
    if (this.file) {
      this.businessService.getBatchId(this.file).pipe(
        (
          this.waitingService.operator<ImportPriceBatchId>()
        ))
        .subscribe(
          ({ batchId, counts }: ImportPriceBatchId) => {
            this.counts = fromPairs(map(counts, ({ status, count }: ImportPriceItemsCount) => [status, count]));
            this.batchId = batchId;
            this.businessService.findFileItems(batchId, STATUS_ERROR).subscribe((result) => {
              this.createErrorBalloons(result);
            });
            this.onTabChanged(STATUS_UPDATED_ITEM);
          },
          this.notificationService.processAll,
        );
    } else if (this.googleSheet) {
      this.businessService.getGoogleBatchId(this.googleSheet).pipe(
        (
          this.waitingService.operator<ImportPriceBatchId>()
        ))
        .subscribe(
          ({ batchId, counts }: ImportPriceBatchId) => {
            this.counts = fromPairs(map(counts, ({ status, count }: ImportPriceItemsCount) => [status, count]));
            this.batchId = batchId;
            this.businessService.findFileItems(batchId, STATUS_ERROR).subscribe((result) => {
              this.createErrorBalloons(result);
            });
            this.onTabChanged(STATUS_UPDATED_ITEM);
          },
          this.notificationService.processAll,
        );
    }
  }

  onFileUpdated(file?: File): void {
    if (!file) {
      this._clearItems();
      this.batchId = '';
    } else {
      this.file = file;
    }
  }

  onGoogleDocumentSelected(files?: GoogleDocument[]): void {
    if (!files) {
      this._clearItems();
      this.batchId = '';
    } else {
      this.googleSheet = files[0];
    }
  }

  onSubmit(): void {
    const firstInvalid = find(this.submitState.newItems, (itemGroup: AppFormGroup) => itemGroup.invalid);
    if (firstInvalid) {
      this.onTabChanged(STATUS_NEW_ITEM);
      // hack as we have no callback after virtual scroll initialization
      // tslint:disable-next-line:no-magic-numbers
      setTimeout(() => this.importPriceNewItemsComponent.scrollTo(firstInvalid), 100);
    } else {
      if (!isNil(this.batchId)) {
        const submitValue = concat(
          map<AppFormGroup, ImportPriceApplyItem>(
            this.submitState.newItems,
            (itemGroup: AppFormGroup): ImportPriceApplyItem => omitApplyItem(itemGroup.getRawValue()),
          ),
          map<ImportPriceApplyItem, ImportPriceApplyItem>(this.submitState.updatedItems, omitApplyItem),
        ).map((item) => (
            {
              code:         item.code,
              isSalesTax:   item.isSalesTax,
              memo:         item.memo,
              name:         item.name,
              status:       item.status,
              uomId:        item.uomId,
              uploadItemId: item.uploadItemId,
            } as ImportPriceApplyItem
          ),
        );

        this.businessService.apply(this.batchId, submitValue).pipe(
          (
            this.waitingService.operator<ImportPriceApplyItem[]>()
          ))
          .subscribe(
            (errors?: any): void => {
              this._clearItems();
              !isEmpty(errors)
              && this.notificationService.processAll(MessageBasket.create(errors));
            },
            (errors?: any): void => {
              const formattedErrors = map(errors.messages, (message: Message) => (
                {
                  ...message,
                  params: {
                    ...message.params,
                    reason: get(message, 'params.reason')
                              && this.translationService.get(<string>get(message, 'params.reason'))
                              || <string>get(message, 'params.errorDescription'),
                  },
                }
              ));
              !isEmpty(formattedErrors)
              && this.notificationService.processAll(MessageBasket.create(formattedErrors));
            },
          );
      }
    }
  }

  onNewItemSelect(itemGroup: AppFormGroup): void {
    this.submitState.newItems.push(itemGroup);
  }

  onNewItemDeselect(deselectItemGroup: AppFormGroup): void {
    remove(
      this.submitState.newItems,
      (itemGroup: AppFormGroup) => deselectItemGroup.getRawValue().code === itemGroup.getRawValue().code,
    );
  }

  onNewItemsSelectAll(isSelectedAll: boolean): void {
    const itemsControls = <FormArray>getControl(this.form.group, 'items');
    this.form.updateSchemaItemGroupsRequired(isSelectedAll);
    this.submitState.newItems = isSelectedAll
      ? clone(<AppFormGroup[]>itemsControls.controls)
      : [];
  }

  onUpdatedItemSelectionChanged($event: MatCheckboxChange, updatedItem: ImportPriceFileItem): void {
    if ($event.checked) {
      this.onUpdatedItemSelected(updatedItem);
    } else {
      this.onUpdatedItemDeselected(updatedItem);
    }
  }

  onUpdatedItemSelected(item: ImportPriceFileItem): void {
    this.submitState.updatedItems.push(item);
  }

  onUpdatedItemDeselected(deselectedItem: ImportPriceFileItem): void {
    remove(
      this.submitState.updatedItems,
      (item: ImportPriceFileItem) => deselectedItem.code === item.code,
    );
  }

  onUpdatedItemsSelectAll($event: MatCheckboxChange): void {
    this.submitState.updatedItems = $event.checked ? clone(this.items[STATUS_UPDATED_ITEM]) : [];
  }

  isUpdatedItemSelected(updatedItem: ImportPriceFileItem): boolean {
    return Boolean(find(this.submitState.updatedItems, (item: ImportPriceFileItem) => item.code === updatedItem.code));
  }

  isUpdatedItemsSelectedAll(): boolean {
    return !isEmpty(this.items[STATUS_UPDATED_ITEM])
      && this.submitState.updatedItems.length === this.items[STATUS_UPDATED_ITEM].length;
  }

  isUpdatedItemsSelectedPartial(): boolean {
    return !isEmpty(this.items[STATUS_UPDATED_ITEM])
      && !isEmpty(this.submitState.updatedItems)
      && this.submitState.updatedItems.length < this.items[STATUS_UPDATED_ITEM].length;
  }

  /**
   * Save new tab to display correct grid
   * @param {TabsNames} status
   */
  onTabChanged(status: TabsNames): void {
    const { batchId } = this;
    const tabHasNoCache = isEmpty(this.items[status]);
    if (tabHasNoCache) {
      this.businessService.findFileItems(batchId, status).pipe(
        (
          this.waitingService.operator<ImportPriceFileItem[]>()
        ),
        map$(createApplyItems(status)),
      )
        .subscribe(
          (items: ImportPriceApplyItem[]) => {
            this.items[status] = items;
            if (status === 'UPDATED_ITEM') {
              this.generateGridDefinition(this.items['UPDATED_ITEM']);
            }
            this.currentTab = status;
            if (eq(status, STATUS_NEW_ITEM)) {
              // build form and mark all for immediate displaying error for required fields if selected
              this.form.build({ items });
              this.form.markAllAsTouched();
            }
          },
          this.notificationService.processAll,
        );
    } else {
      if (status === 'UPDATED_ITEM') {
        this.generateGridDefinition(this.items['UPDATED_ITEM']);
      }
      this.currentTab = status;
    }
  }

  interpolateErrorCodes(codes: number[], rowNumber: string): string {
    return join(
      map(codes, (code: number) =>
        this.translationService.get(`lbl_import_price_error_code:${ code }`, { row_number: rowNumber })),
      ', ',
    );
  }

  isCurrentTab(name: TabsNames): boolean {
    const { currentTab } = this;
    return eq(name, currentTab);
  }

  itemsCount(type: TabsNames): number {
    return this.counts[type];
  }

  _clearItems(): void {
    this.items = {
      [STATUS_UPDATED_ITEM]: [],
      [STATUS_NEW_ITEM]:     [],
      [STATUS_ERROR]:        [],
    };
    this.submitState = {
      newItems:     [],
      updatedItems: [],
    };
    this.currentTab = STATUS_UPDATED_ITEM;
    this.batchId = '';
    this.file = void 0;
    this.googleSheet = void 0;
  }

  createErrorBalloons(errorResponse: any[]): void {
    errorResponse.forEach((error) => {
      error.errorCodes.forEach((code: number) => {
        const text = this.translationService.get(`lbl_import_price_error_code:${ code }`, { row_number: error.batchRowNumber });
        this.notificationService.warning(text);
      });
    });
  }

  clearFile(): void {
    this.dialogService.confirmation(yesNoConfirmation('msg_import_prices_data_will_be_lost')).pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        this._clearItems();
      });
  }

  generateGridDefinition(items: ImportPriceApplyItem[]): void {
    const model = createDefaultGridModel({
      editable:   true,
      getId:      (item: ImportPriceApplyItem) => item.uploadItemId,
      getCellDef: this.importPriceApplyItemState,
      validate:   this.importPriceApplyValidateField,
      command:    (entity: ImportPriceApplyItem, field, value) => this.setThisItem({ entity, field, value }),
    });
    const colDefs = [{
      field:           'code',
      headerName:      this.translationService.get('lbl_code'),
      width:           110,
      suppressMovable: true,
      filter:          false,
    }, {
      field:           'currentCost',
      headerName:      this.translationService.get('lbl_current_price'),
      width:           140,
      suppressMovable: true,
      filter:          false,
    }, {
      field:           'cost',
      headerName:      this.translationService.get('lbl_new_price'),
      width:           140,
      suppressMovable: true,
      filter:          false,
    }, {
      field:           'currentName',
      headerName:      this.translationService.get('lbl_current_description'),
      width:           200,
      suppressMovable: true,
      filter:          false,
    }, {
      field: 'name',
      ...buildEditableMultilineTextColumn(TextTypes.Text255, model.getCellAccessor('name'), {
        minRows:            3, maxRows: 10,
        hasAlternativeText: (params) => !params.data.name || params.data.name === params.data.currentName,
        getAlternativeText: constant(this.translationService.get('lbl_no_changes')),
      }),
      headerName:      this.translationService.get('lbl_new_description'),
      width:           200,
      suppressMovable: true,
      filter:          false,
    }, {
      field:           'currentUomId',
      headerName:      this.translationService.get('lbl_current_uom_abbreviation'),
      width:           200,
      suppressMovable: true,
      filter:          false,
      valueGetter:     ({ data }: { data: ImportPriceApplyItem }) => {
        const targetUom = this.uoms.find((uom) => uom.id === data.currentUomId);
        if (targetUom) {
          return targetUom.name;
        }
        return '';
      },
    }, {
      field: 'uomId',
      ...buildEditableSelectColumn(
        buildOptionSetFromArray(this.uoms),
        model.getCellAccessor('uomId'),
        {
          hasAlternativeText: (params) => !params.data.uomId || params.data.uomId === params.data.currentUomId,
          getAlternativeText: constant(this.translationService.get('lbl_no_changes')),
        },
      ),
      headerName:      this.translationService.get('lbl_new_uom_abbreviation'),
      width:           200,
      suppressMovable: true,
      filter:          false,
    }, {
      field:           'currentMemo',
      headerName:      this.translationService.get('lbl_current_memo'),
      width:           200,
      suppressMovable: true,
      filter:          false,
    }, {
      field: 'memo',
      ...buildEditableMultilineTextColumn(TextTypes.Text255, model.getCellAccessor('memo'), {
        minRows:            3, maxRows: 10,
        hasAlternativeText: (params) => !params.data.memo || params.data.memo === params.data.currentMemo,
        getAlternativeText: constant(this.translationService.get('lbl_no_changes')),
      }),
      headerName:      this.translationService.get('lbl_new_memo'),
      width:           200,
      suppressMovable: true,
      filter:          false,
    }, {
      field:           'variance',
      headerName:      this.translationService.get('lbl_variance_dollar'),
      width:           120,
      suppressMovable: true,
      filter:          false,
    }, {
      field:           'variancePct',
      headerName:      this.translationService.get('lbl_variance_percent'),
      width:           120,
      suppressMovable: true,
      filter:          false,
      cellClass:       ({ data }: { data: ImportPriceFileItem }) => data.isUp ? 'cell--negative' : 'cell--positive',
    }, {
      field:           'isUp',
      headerName:      this.translationService.get('lbl_trend'),
      width:           100,
      suppressMovable: true,
      filter:          false,
      cellRenderer:    (params: any): string => params.node.data.isUp
        ? renderColoredArrow(PINK_MARKER_COLOR)
        : renderColoredArrow(LIGHTGREEN_MARKER_COLOR, false),
    }] as any[];

    this.updatedItemsGridDefinition = this.gridDefinitionFactory.buildManualWithModel({
        model,
        columns: colDefs,
        options: {
          getRowNodeId: (data) => data.uploadItemId,
        },
      },
    );
  }

  private setThisItem({ entity, field, value }: any): void {
    const updatedItem = this.items[STATUS_UPDATED_ITEM].find((item) => item.uploadItemId === entity.uploadItemId);
    if (updatedItem) {
      updatedItem[field] = value;
    }
  }
}
